import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success("Password reset successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl mb-4">INVALID RESET LINK</h2>
          <p className="text-[#A3A3A3] mb-6">This link is invalid or has expired.</p>
          <Link to="/forgot-password" className="btn-primary inline-block">Request New Link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 flex items-center justify-center" data-testid="reset-password-page">
      <SEO title="Reset Password" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="border border-white/10 p-8 md:p-12">
          <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-2">ACCOUNT RECOVERY</p>
          <h1 className="font-heading text-3xl mb-6">NEW PASSWORD</h1>

          {done ? (
            <div data-testid="reset-done-message">
              <p className="text-[#C0C0C0] text-sm leading-relaxed mb-6">
                Your password has been reset. You can now log in with your new password.
              </p>
              <Link to="/auth" className="btn-primary w-full block text-center" data-testid="go-to-login-btn">
                Go to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="relative flex items-center">
                <Lock className="absolute left-0 w-5 h-5 text-[#A3A3A3]" />
                <input
                  type="password"
                  placeholder="New Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-transparent border-b-2 border-white/20 py-4 pl-10 pr-4 text-lg text-white placeholder-white/30 outline-none focus:border-[#FFD700] transition-colors"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>
              <div className="relative flex items-center">
                <Lock className="absolute left-0 w-5 h-5 text-[#A3A3A3]" />
                <input
                  type="password"
                  placeholder="Confirm Password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full bg-transparent border-b-2 border-white/20 py-4 pl-10 pr-4 text-lg text-white placeholder-white/30 outline-none focus:border-[#FFD700] transition-colors"
                  required
                  minLength={6}
                  data-testid="confirm-password-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-3"
                data-testid="reset-password-btn"
              >
                {loading ? <div className="loader w-5 h-5 border-2" /> : "Reset Password"}
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
